/* ============================================================================
   ERROR BOUNDARY — a rendering error used to take the whole screen with it:
   React unmounts the tree and the person on site is left looking at a blank
   page with no idea what happened. Now the page stays, says what broke, and
   offers the two things that actually help — try again, or reload — while
   the details are kept so the studio can read them.
   ========================================================================== */
import { Component, type ReactNode } from "react";

interface State { error: Error | null; info?: string }
const KEY = "atelier.lastError";

/* who and which build — read straight from storage and the page, so this file
   pulls in none of the app's stores (it must work when they are what broke) */
function whoIsThis(): string {
  try {
    const s = JSON.parse(localStorage.getItem("bidroom.session.v1") || "null") as { role?: string; name?: string; company?: string } | null;
    if (!s?.role) return "";
    return `${s.name || "?"}${s.company ? ` (${s.company})` : ""} · ${s.role}`;
  } catch { return ""; }
}
function buildId(): string {
  try {
    const src = [...document.querySelectorAll<HTMLScriptElement>("script[type=module][src]")].map((s) => s.src).find((u) => /\/assets\/index-/.test(u));
    return src ? src.replace(/^.*\/assets\/index-|\.js$/g, "") : "dev";
  } catch { return ""; }
}
/* the same error repeating in a loop is sent three times a page load, and at
   most 25 reports go out per page load in all */
const sentCounts = new Map<string, number>();
let sentTotal = 0;
const retryQueueKey = "atelier.crashQueue";
function post(payload: object): Promise<boolean> {
  return fetch("/api/client-error", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload), keepalive: true })
    .then((r) => r.ok).catch(() => false);
}
export function recordClientError(message: string, detail?: string) {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || "[]") as { at: string; message: string; detail?: string; url: string; ua: string }[];
    list.unshift({ at: new Date().toISOString(), message, detail: detail?.slice(0, 2000), url: location.href, ua: navigator.userAgent });
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, 10)));
  } catch { /* storage unavailable */ }
  const k = message.slice(0, 120);
  const n = (sentCounts.get(k) ?? 0) + 1;
  sentCounts.set(k, n);
  if (n > 3 || ++sentTotal > 25) return;
  const payload = { message, detail: detail?.slice(0, 4000), url: location.href, ua: navigator.userAgent, who: whoIsThis(), build: buildId(), at: Date.now() };
  // offline on site: keep it and send it when the connection is back
  void post(payload).then((ok) => {
    if (ok) return;
    try {
      const q = JSON.parse(localStorage.getItem(retryQueueKey) || "[]") as object[];
      q.push(payload);
      localStorage.setItem(retryQueueKey, JSON.stringify(q.slice(-10)));
    } catch { /* storage unavailable */ }
  });
}
/** Send crash reports that were held while offline. Called once at start. */
export function flushHeldCrashReports() {
  let q: object[] = [];
  try { q = JSON.parse(localStorage.getItem(retryQueueKey) || "[]"); localStorage.removeItem(retryQueueKey); } catch { return; }
  for (const p of q.slice(-10)) void post(p);
}

export class ErrorBoundary extends Component<{ children: ReactNode; inline?: boolean }, State> {
  state: State = { error: null };
  static getDerivedStateFromError(error: Error): State { return { error }; }
  private domRetries = 0;
  componentDidCatch(error: Error, info: { componentStack?: string }) {
    this.setState({ info: info.componentStack ?? "" });
    recordClientError(`${error.name}: ${error.message}`, `${error.stack ?? ""}\n${info.componentStack ?? ""}`);
    // a page file from an older build is gone after a deploy — load the new build
    if (/dynamically imported module|Importing a module script failed|error loading dynamically imported|Failed to fetch dynamically/i.test(error.message)) {
      const reload = (window as unknown as { __reloadForNewBuild?: () => boolean }).__reloadForNewBuild;
      if (reload?.()) return;
    }
    // a page rewritten by browser translation — recover in place rather than
    // leave the crew on an error screen

    if (/removeChild|insertBefore|not a child of this node/.test(error.message) && this.domRetries < 3) {
      this.domRetries++;
      setTimeout(() => this.setState({ error: null, info: undefined }), 50);
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div style={{ minHeight: this.props.inline ? 360 : "100vh", display: "grid", placeItems: "center", padding: 24, background: "var(--paper, #f6f3ee)", color: "var(--ink, #1b1812)", fontFamily: "inherit" }}>
        <div style={{ maxWidth: 480, width: "100%", background: "var(--card, #fff)", border: "1px solid var(--line, #e6e1d8)", borderRadius: 18, padding: 26 }}>
          <div style={{ fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--ink-3, #8a847a)" }}>Something went wrong</div>
          <h1 style={{ fontSize: 24, margin: "8px 0 10px", fontWeight: 600 }}>This page hit an error</h1>
          <p style={{ fontSize: 14, lineHeight: 1.6, margin: 0, color: "var(--ink-2, #4a453d)" }}>
            Your work is saved on this device up to the last change. Try again to go back to where you were, or reload the page. The details below have been kept for the studio.
          </p>
          <pre style={{ fontSize: 11, lineHeight: 1.45, background: "var(--paper-2, #f0ece5)", borderRadius: 10, padding: "10px 12px", margin: "14px 0", whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 160, overflow: "auto" }}>{error.name}: {error.message}</pre>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn accent" onClick={() => this.setState({ error: null, info: undefined })}>Try again</button>
            <button className="btn ghost" onClick={() => location.reload()}>Reload the page</button>
            <button className="btn ghost" onClick={() => { void navigator.clipboard?.writeText(`${error.name}: ${error.message}\n${error.stack ?? ""}\n${this.state.info ?? ""}\n${location.href}\n${navigator.userAgent}`); }}>Copy details</button>
          </div>
        </div>
      </div>
    );
  }
}
